import Typography from "@mui/material/Typography";
import { keyframes } from "@mui/material";
import { styled } from "@mui/material/styles";
import Percent from "_shared/percentages/percent";
import { enqueueSnackbar } from "notistack";
import ProposalIssues from "work/proposals/issues/proposal-issues";
import ProposalField, { ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";
import FieldRedline from "work/proposals/redlining/field-redlines/field-redline";
import ProposalRedline, { ProposalRedlineUpdate } from "work/proposals/redlining/proposal-redline";
import PercentRedlineInput from "work/proposals/redlining/field-redlines/_views/percent-redline-input";
import { Masked } from "work/masked-fields";
import ProposalInfoTooltip from "work/proposals/_views/proposal-info-tooltip";
import { ReviewerEditLockAdornment } from "work/proposals/reviewers/_views/reviewer-edit-lock-feedback";
import { MaskedProposalField, maskedProposalFieldNotice } from "work/proposals/_views/_shared/masked-field-notice";

const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 1px rgba(21, 101, 192, 0.1);
    }
    50% {
    border-color: rgb(21, 101, 192);
    box-shadow: 0 0 0 3px rgba(21, 101, 192, 0.33);
  }
`;
const ProposalDiscountContainer = styled("div")(({ theme }) => ({
  alignContent: "center",
  height: "100%",
  margin: theme.spacing(3),
}));
const HighlightRedlinePercentInput = styled(PercentRedlineInput, { shouldForwardProp: (prop) => prop !== "active" })<{
  active?: boolean;
}>(({ active }) => ({
  ...(active && {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        animation: `${pulseBorder} 1.5s ease-in-out infinite`,
      },
    },
  }),
}));
const HelperText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

const discountDescription =
  "You can include the AttorneyHub fees you incur from a project by establishing " +
  'an agreed upon "discount" from your vendor. 2.912% is the value to cover the cost.';

type DiscountTabProps = {
  proposalRedline: ProposalRedline;
  issues?: ProposalIssues;
  activeReviewField: ProposalField | undefined;
  disableEditing?: boolean;
  fieldLockTooltip?: string;
  onProposalRedlineChange: (newRedline: ProposalRedlineUpdate, traversalFieldOverride?: ProposalField | null) => void;
  onTraverseToNewField: (fieldInfo: ProposalField | undefined) => void;
  negotiationId?: string;
  viewerScope?: string;
};

const DiscountTab = (props: Readonly<DiscountTabProps>) => {
  const {
    proposalRedline,
    activeReviewField,
    disableEditing,
    fieldLockTooltip,
    onProposalRedlineChange,
    onTraverseToNewField,
    negotiationId,
    viewerScope,
  } = props;

  if (Masked.isMasked(proposalRedline.discount)) return maskedProposalFieldNotice(MaskedProposalField.Discount);
  const lockAdornment =
    disableEditing && fieldLockTooltip ? <ReviewerEditLockAdornment title={fieldLockTooltip} /> : undefined;

  return (
    proposalRedline && (
      <ProposalDiscountContainer>
        <HighlightRedlinePercentInput
          focused={activeReviewField?.name === ProposalFieldName.Discount}
          active={activeReviewField?.isEqualTo(ProposalField.Discount)}
          lockAdornment={lockAdornment}
          readOnly={disableEditing}
          disabled={disableEditing}
          originalPercentFieldRedline={proposalRedline.discount}
          negotiationId={negotiationId}
          viewerScope={viewerScope}
          onPercentFieldRedlineChange={(
            updatedRedline: FieldRedline<Percent>,
            traversalFieldOverride?: ProposalField | null
          ) => {
            try {
              onProposalRedlineChange(
                (currentRedline) => currentRedline.updateDiscountRedline(updatedRedline),
                traversalFieldOverride,
              );
            } catch (error) {
              console.error("Error updating discount", error);
              enqueueSnackbar("Error updating discount", { variant: "error" });
            }
          }}
          onTraverseIn={() => {
            onTraverseToNewField(ProposalField.Discount);
          }}
        />
        <HelperText>
          A discount of <b>3%</b> will cover subscription costs.
          <ProposalInfoTooltip title={discountDescription} />
        </HelperText>
      </ProposalDiscountContainer>
    )
  );
};

export default DiscountTab;
